/**
 * Accounting tab — accountant's worklist of items pending entry into the
 * external books of account.
 *
 * Three sub-queues (Invoices / Payments / Receipts) × (Pending / Entered).
 * Tournament filter defaults to the active tournament. Date range, search,
 * bulk-tick, and CSV export are all supported.
 *
 * Each row carries an audit log popover showing who marked it (and when).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { BookOpen, Download, History, CheckCircle2, Undo2, Filter, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KINDS = [
  { key: 'invoices', label: 'Purchase Invoices' },
  { key: 'payments', label: 'Payments Made' },
  { key: 'receipts', label: 'Receipts Received' },
];

const fmt = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-IN', { maximumFractionDigits: 2 })
    : n || '0';

const dateOnly = (d) => (d ? String(d).slice(0, 10) : '—');

// Map a row to the page that has the most context for an accountant.
// Invoice has a dedicated detail page; Payments live on /bank (the page is
// the detail surface) and Receipts live on /revenue. We add `?highlight=<id>`
// so the listing page can scroll-into-view + open the right tab.
const detailLinkFor = (it) => {
  if (!it?.id) return null;
  if (it.kind === 'invoice') return `/invoices/${it.id}`;
  if (it.kind === 'payment') return `/bank?highlight=${encodeURIComponent(it.id)}`;
  if (it.kind === 'receipt') return `/revenue?highlight=${encodeURIComponent(it.id)}`;
  return null;
};

// Reusable link cell — renders an external-tab link with the doc number and
// a tiny arrow glyph so the affordance is obvious. Falls back to plain text
// if the row somehow has no id.
const DocLink = ({ it, label }) => {
  const href = detailLinkFor(it);
  if (!href) return <span className="font-mono">{label || '—'}</span>;
  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
      title={`Open ${it.kind} detail in a new tab`}
      data-testid={`acc-link-${it.id}`}
    >
      <span className="truncate max-w-[180px]">{label || '—'}</span>
      <ExternalLink className="h-3 w-3 opacity-70" />
    </Link>
  );
};

const Accounting = () => {
  const { user } = useAuth();
  const [activeKind, setActiveKind] = useState('invoices');
  const [activeStatus, setActiveStatus] = useState('pending');
  const [tournaments, setTournaments] = useState([]);
  const [tournamentFilter, setTournamentFilter] = useState(''); // '' = all
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({}); // {invoices_pending: n, invoices_entered: n, …}
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  // Load tournaments + default to the active one on first paint
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/tournaments`, { withCredentials: true });
        const list = res?.data?.tournaments || [];
        setTournaments(list);
        const def =
          list.find((t) => t.is_default && t.is_active) ||
          list.find((t) => t.is_default) ||
          list[0];
        if (def?.name) setTournamentFilter(def.name);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = { kind: activeKind, status: activeStatus };
      if (tournamentFilter) params.tournament = tournamentFilter;
      if (dateFrom) params.date_from = `${dateFrom}T00:00:00Z`;
      if (dateTo) params.date_to = `${dateTo}T23:59:59Z`;
      const res = await axios.get(`${API}/accounting/list`, {
        withCredentials: true,
        params,
      });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load list');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Live count fetch for the sub-tab badges
  const fetchCounts = async () => {
    try {
      const baseParams = {};
      if (tournamentFilter) baseParams.tournament = tournamentFilter;
      if (dateFrom) baseParams.date_from = `${dateFrom}T00:00:00Z`;
      if (dateTo) baseParams.date_to = `${dateTo}T23:59:59Z`;
      const next = {};
      await Promise.all(
        KINDS.flatMap((k) =>
          ['pending', 'entered'].map(async (s) => {
            try {
              const res = await axios.get(`${API}/accounting/list`, {
                withCredentials: true,
                params: { ...baseParams, kind: k.key, status: s },
              });
              next[`${k.key}_${s}`] = res.data?.count ?? 0;
            } catch {
              next[`${k.key}_${s}`] = 0;
            }
          })
        )
      );
      setCounts(next);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKind, activeStatus, tournamentFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentFilter, dateFrom, dateTo]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (it) =>
        (it.doc_number || '').toLowerCase().includes(q) ||
        (it.party_name || '').toLowerCase().includes(q) ||
        (it.invoice_number || '').toLowerCase().includes(q) ||
        (it.narration || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const allSelected = filtered.length > 0 && filtered.every((it) => selected.has(it.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((it) => it.id)));
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const kindSingular = useMemo(() => {
    if (activeKind === 'invoices') return 'invoice';
    if (activeKind === 'payments') return 'payment';
    return 'receipt';
  }, [activeKind]);

  const handleMark = async (rows, intoStatus) => {
    if (!rows.length) return;
    setBusy(true);
    try {
      const url = intoStatus === 'entered' ? '/accounting/mark-entered' : '/accounting/unmark-entered';
      const res = await axios.post(
        `${API}${url}`,
        { items: rows.map((r) => ({ kind: kindSingular, id: r.id })) },
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Updated');
      setSelected(new Set());
      await Promise.all([fetchItems(), fetchCounts()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = { kind: activeKind, status: activeStatus };
      if (tournamentFilter) params.tournament = tournamentFilter;
      if (dateFrom) params.date_from = `${dateFrom}T00:00:00Z`;
      if (dateTo) params.date_to = `${dateTo}T23:59:59Z`;
      const res = await axios.get(`${API}/accounting/export-csv`, {
        withCredentials: true,
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accounting_${activeKind}_${activeStatus}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    }
  };

  const headerForKind = () => {
    if (activeKind === 'invoices') {
      return ['Date', 'Invoice #', 'Vendor', 'Tournament', 'Dept / Head', 'Taxable', 'GST', 'TDS', 'Net Payable', 'Books'];
    }
    if (activeKind === 'payments') {
      return ['Date', 'Reference', 'Vendor', 'Tournament', 'Mode', 'Amount', 'Books'];
    }
    if (activeKind === 'receipts') {
      return ['Date', 'Receipt #', 'Linked Invoice', 'Team', 'Tournament', 'Mode', 'Amount', 'Books'];
    }
    return [];
  };

  const renderRow = (it) => {
    const checked = selected.has(it.id);
    const isEntered = it.books_status === 'entered';
    return (
      <tr
        key={it.id}
        data-testid={`acc-row-${it.id}`}
        className={`border-b border-border hover:bg-muted/40 ${isEntered ? 'opacity-70' : ''}`}
      >
        <td className="py-2 pl-3 pr-2">
          <Checkbox
            checked={checked}
            onCheckedChange={() => toggleOne(it.id)}
            data-testid={`acc-row-check-${it.id}`}
          />
        </td>
        <td className="py-2 px-2 text-xs whitespace-nowrap">{dateOnly(it.date)}</td>
        {activeKind === 'invoices' && (
          <>
            <td className="py-2 px-2 text-xs">
              <DocLink it={it} label={it.doc_number} />
            </td>
            <td className="py-2 px-2 text-xs">{it.party_name || '—'}</td>
            <td className="py-2 px-2 text-xs">{it.tournament || '—'}</td>
            <td className="py-2 px-2 text-xs whitespace-normal min-w-[120px]">
              <div className="font-medium">{it.department || '—'}</div>
              <div className="text-muted-foreground text-[10px] leading-tight">{it.budget_head || '—'}</div>
            </td>
            <td className="py-2 px-2 text-xs text-right">{fmt(it.taxable_value)}</td>
            <td className="py-2 px-2 text-xs text-right">
              {fmt((it.cgst || 0) + (it.sgst || 0) + (it.igst || 0))}
            </td>
            <td className="py-2 px-2 text-xs text-right">{fmt(it.tds)}</td>
            <td className="py-2 px-2 text-xs text-right font-semibold">{fmt(it.net_payable)}</td>
          </>
        )}
        {activeKind === 'payments' && (
          <>
            <td className="py-2 px-2 text-xs">
              <DocLink it={it} label={it.doc_number} />
            </td>
            <td className="py-2 px-2 text-xs">{it.party_name || '—'}</td>
            <td className="py-2 px-2 text-xs">{it.tournament || '—'}</td>
            <td className="py-2 px-2 text-xs">{it.payment_mode || '—'}</td>
            <td className="py-2 px-2 text-xs text-right font-semibold">{fmt(it.amount)}</td>
          </>
        )}
        {activeKind === 'receipts' && (
          <>
            <td className="py-2 px-2 text-xs">
              <DocLink it={it} label={it.doc_number} />
            </td>
            <td className="py-2 px-2 text-xs">{it.invoice_number || '—'}</td>
            <td className="py-2 px-2 text-xs">{it.party_name || '—'}</td>
            <td className="py-2 px-2 text-xs">{it.tournament || '—'}</td>
            <td className="py-2 px-2 text-xs">{it.payment_method || '—'}</td>
            <td className="py-2 px-2 text-xs text-right font-semibold">{fmt(it.amount)}</td>
          </>
        )}
        <td className="py-2 px-2 text-xs">
          <div className="flex items-center gap-1">
            <Badge
              variant="outline"
              className={
                isEntered
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800 h-5 px-1.5 text-[10px]'
                  : 'bg-amber-50 border-amber-200 text-amber-800 h-5 px-1.5 text-[10px]'
              }
            >
              {isEntered ? 'Entered' : 'Pending'}
            </Badge>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  title="View entry log"
                  data-testid={`acc-log-${it.id}`}
                >
                  <History className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="left" className="w-72 text-xs">
                <div className="font-semibold mb-2">Entry log</div>
                {(it.books_log || []).length === 0 ? (
                  <div className="text-muted-foreground">No entries yet</div>
                ) : (
                  <ul className="space-y-2 max-h-60 overflow-auto">
                    {[...(it.books_log || [])].reverse().map((l, idx) => (
                      <li key={l.at || `log-${idx}`} className="border-l-2 pl-2 border-emerald-300">
                        <div className="font-medium capitalize">{l.action}</div>
                        <div className="text-muted-foreground">
                          by {l.by} · {dateOnly(l.at)} {String(l.at || '').slice(11, 19)}
                        </div>
                        {l.note && <div className="text-muted-foreground italic">"{l.note}"</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </PopoverContent>
            </Popover>
            {isEntered ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                title="Move back to pending"
                onClick={() => handleMark([it], 'pending')}
                disabled={busy}
                data-testid={`acc-unmark-${it.id}`}
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-emerald-700"
                title="Mark entered in books"
                onClick={() => handleMark([it], 'entered')}
                disabled={busy}
                data-testid={`acc-mark-${it.id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const headers = headerForKind();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-heading flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-primary" />
            Accounting
          </h1>
          <p className="text-muted-foreground mt-1">
            Queue of finalised activity for the accountant to enter into Tally / books.
            Tick to mark entered. Every action is logged.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <Label className="text-xs">Tournament</Label>
              <Select
                value={tournamentFilter || '__all__'}
                onValueChange={(v) => setTournamentFilter(v === '__all__' ? '' : v)}
              >
                <SelectTrigger data-testid="acc-tournament-filter">
                  <SelectValue placeholder="All tournaments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All tournaments</SelectItem>
                  {tournaments.map((t) => (
                    <SelectItem key={t.id || t.name} value={t.name}>
                      {t.name}
                      {t.is_default ? ' (default)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="acc-date-from"
                className="w-40"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="acc-date-to"
                className="w-40"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <Input
                placeholder="Doc #, party, narration…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="acc-search"
              />
            </div>
            {(dateFrom || dateTo || search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setSearch('');
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sub-tabs: Invoices / Payments / Receipts */}
      <Tabs value={activeKind} onValueChange={setActiveKind}>
        <TabsList className="w-full grid grid-cols-3">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`acc-kind-${k.key}`}>
              {k.label}
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
                <span className="px-1.5 rounded bg-amber-100 text-amber-900 font-bold">
                  {counts[`${k.key}_pending`] ?? 0}
                </span>
                <span className="px-1.5 rounded bg-emerald-100 text-emerald-900 font-bold">
                  {counts[`${k.key}_entered`] ?? 0}
                </span>
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key}>
            <Card className="border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-base">{k.label}</CardTitle>
                    <CardDescription>
                      {activeStatus === 'pending'
                        ? `${filtered.length} item(s) awaiting entry into books`
                        : `${filtered.length} item(s) already entered`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tabs value={activeStatus} onValueChange={setActiveStatus}>
                      <TabsList>
                        <TabsTrigger value="pending" data-testid="acc-status-pending">
                          Pending
                        </TabsTrigger>
                        <TabsTrigger value="entered" data-testid="acc-status-entered">
                          Entered
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={exportCsv}
                      data-testid="acc-export-csv"
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      Export CSV
                    </Button>
                    {selected.size > 0 && (
                      <Button
                        size="sm"
                        onClick={() =>
                          handleMark(
                            filtered.filter((r) => selected.has(r.id)),
                            activeStatus === 'pending' ? 'entered' : 'pending'
                          )
                        }
                        disabled={busy}
                        data-testid="acc-bulk-mark"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                        {activeStatus === 'pending'
                          ? `Mark ${selected.size} as Entered`
                          : `Move ${selected.size} to Pending`}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
                ) : filtered.length === 0 ? (
                  <div className="py-12 text-center">
                    <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground text-sm">
                      {activeStatus === 'pending'
                        ? 'Nothing pending. Great job staying on top of the books! 🎉'
                        : 'No items have been marked entered yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="py-2 pl-3 pr-2 text-left w-10">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={toggleAll}
                              data-testid="acc-select-all"
                            />
                          </th>
                          {headers.map((h) => (
                            <th
                              key={h}
                              className={`py-2 px-2 ${
                                ['Taxable', 'GST', 'TDS', 'Net Payable', 'Amount'].includes(h)
                                  ? 'text-right'
                                  : 'text-left'
                              }`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>{filtered.map(renderRow)}</tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default Accounting;
